/**
 * LANE W-NAV — the side-nav drawer's pure logic (Jason, from a live
 * screenshot's pink top-left annotation: "I need upper left navigation on
 * every page it can trigger the side nav bar in from the left but we need
 * something besides the bottom only nav"). Kept framework-free (no React, no
 * react-router) so both the nav-items list and the current-route highlight
 * decision are directly unit-testable without mounting a component — this
 * repo has no jsdom/React-Testing-Library setup, and every other *.tsx
 * decision in this app follows the same "extract the pure logic into its own
 * module" idiom (homeRouteGate.ts / goodbyeRoute.ts / onboardingStep.ts /
 * jobBuckets.ts / dragRehome.ts).
 *
 * ADDITIVE, not a replacement: the bottom TabBar (TabBar.tsx) stays exactly
 * as-is. This is a second, upper-left-triggered way to reach the SAME
 * destinations (Jason's own words: "besides the bottom only nav") plus
 * Settings and Sign out, which the bottom bar has no room for.
 */

export interface SideNavItem {
  to: string;
  /** Exact-match route (mirrors react-router's NavLink `end` prop / TabBar's
   *  own `end` field) — only '/' needs this; every other route is a prefix
   *  match so a sub-route (e.g. /jobs/:jobId) still highlights its parent. */
  end: boolean;
  label: string;
}

/**
 * The drawer's primary destinations — DELIBERATELY the exact same 5 routes
 * TabBar.tsx's TABS array already defines (Home / Refine / Get Found / Jobs /
 * Track), in the same order, PLUS Settings (which the bottom bar has no slot
 * for — Settings is reached today only via Home's top-right gear icon). This
 * is a parallel list rather than an import of TabBar's TABS constant because
 * TabBar's entries carry inline SVG icon JSX (a React concern) alongside the
 * route data — importing it here would pull React into a file that must stay
 * framework-free for direct unit testing. sideNav.themeCompliance.test.ts and
 * this file's own pin test are what keep the two lists from silently
 * drifting apart (see SIDE_NAV_ITEMS' own pinned route-order test).
 */
export const SIDE_NAV_ITEMS: SideNavItem[] = [
  { to: '/', end: true, label: 'Home' },
  { to: '/refine', end: false, label: 'Refine' },
  { to: '/profiles', end: false, label: 'Get Found' },
  { to: '/jobs', end: false, label: 'Jobs' },
  { to: '/track', end: false, label: 'Track' },
  { to: '/settings', end: false, label: 'Settings' },
];

/**
 * Whether a given nav item should render as the "current route" row —
 * mirrors react-router's own NavLink `isActive` semantics exactly (an `end`
 * item matches only the exact pathname; a non-`end` item matches the
 * pathname itself OR any deeper sub-path), so the drawer's highlight always
 * agrees with what NavLink would compute for the SAME route if it were
 * rendered as a NavLink directly. Pulled out as its own pure function (rather
 * than inlined in the component) purely so it has direct pin coverage —
 * NavLink itself is not used here because the drawer closes on click via an
 * onClick side-effect (see SideNavDrawer.tsx), which a plain react-router
 * `<Link>` composes more simply than overriding NavLink's className render
 * prop would.
 */
export function isSideNavItemActive(item: SideNavItem, pathname: string): boolean {
  if (item.end) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/**
 * The drawer's open/close state machine — deliberately tiny (open/closed is
 * the entire state space) but pulled out as a named, pin-tested decision
 * anyway: SideNavDrawer.tsx composes this exact toggle/close pair behind
 * useState rather than hand-rolling the boolean flips inline, so the "what
 * does a trigger tap do when already open" / "does a nav tap always close,
 * never toggle" questions have one traceable answer instead of being
 * re-derived from reading JSX.
 */
export type SideNavState = 'closed' | 'open';

export function toggleSideNav(state: SideNavState): SideNavState {
  return state === 'open' ? 'closed' : 'open';
}

/** Every closing trigger (a nav-row tap, the scrim, the X, Esc) resolves to
 *  the SAME closed state — named so the four call sites in SideNavDrawer.tsx
 *  all read as "the same decision," not four independent setState('closed')
 *  calls that happen to agree today. */
export function closeSideNav(): SideNavState {
  return 'closed';
}
